import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import { toastUtil } from '../../utils/toast'
import { dialogUtil } from '../../utils/dialog'
import AdminSidebar from '../components/AdminSidebar'
import AdminInput from '../components/AdminInput'
import type { PushPermissionStats } from '../api/push'

const AdminPush: React.FC = () => {
  const navigate = useNavigate()
  const [audience, setAudience] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [perm, setPerm] = useState<PushPermissionStats | null>(null)

  // 알림 현황은 없어도 발송은 된다. 실패해도 조용히 둔다.
  const fetchPerm = useCallback(async () => {
    try {
      const res = await adminApi.getPushPermissionStats()
      setPerm(res.data)
    } catch {
      setPerm(null)
    }
  }, [])

  const fetchAudience = useCallback(async () => {
    try {
      const res = await adminApi.getPushAudience()
      setAudience(res.data.audience)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('발송 대상 수를 불러오지 못했습니다')
      }
    }
  }, [navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchAudience()
    fetchPerm()
  }, [navigate, fetchAudience, fetchPerm])

  const handleSend = async () => {
    if (!title.trim()) {
      toastUtil.error('제목을 입력해주세요')
      return
    }
    if (!body.trim()) {
      toastUtil.error('본문을 입력해주세요')
      return
    }

    const confirmed = await dialogUtil.confirm({
      title: '광고 푸시 발송',
      message: `푸시 알림을 켜둔 ${
        audience ?? '?'
      }명에게 발송합니다.\n발송 후 취소할 수 없습니다. 계속할까요?`,
      confirmText: '발송',
      cancelText: '취소',
    })
    if (!confirmed) return

    setIsSending(true)
    try {
      const res = await adminApi.sendPushBroadcast({
        title: title.trim(),
        body: body.trim(),
      })
      toastUtil.success(res.message)
      setTitle('')
      setBody('')
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error(
          error.response?.data?.message || '발송 중 오류가 발생했습니다'
        )
      }
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-backgroundColorBg2">
      <AdminSidebar />
      <main className="flex-1 p-32px">
        <div className="mb-24px">
          <h2 className="text-style-title2 text-textColorText2">광고 푸시 발송</h2>
          <p className="text-style-body2 text-textColorText2 mt-4px">
            푸시 알림을 켜둔 유저 전체에게 광고/프로모션 알림을 보냅니다.
          </p>
        </div>

        {/*
          알림 현황 — 휴대폰 알림 허용과 광고 동의는 다른 값이다.
          광고는 '광고 동의'한 사람에게만 가고, 그중 휴대폰 알림을 끈 사람에게는 화면에 안 뜬다.
        */}
        {perm && (
          <div className="max-w-560px bg-white rounded-12px border border-brandPrimary-10% p-24px mb-20px">
            <div className="flex items-baseline justify-between mb-16px">
              <h3 className="text-style-title3 text-textColorText2">알림 현황</h3>
              <span className="text-style-captionM text-textColorText3">
                활성 회원 {perm.total}명 기준
              </span>
            </div>

            <div className="grid grid-cols-2 gap-12px">
              <div className="bg-backgroundColorBg2 rounded-8px px-16px py-12px">
                <p className="text-style-captionM text-textColorText3">휴대폰 알림 허용</p>
                <p className="text-style-title3 text-brown-80%-main mt-4px">{perm.granted}명</p>
                <p className="text-style-captionM text-textColorText3 mt-4px">
                  거부 {perm.denied}명 · 모름 {perm.unknown}명
                </p>
              </div>
              <div className="bg-backgroundColorBg2 rounded-8px px-16px py-12px">
                <p className="text-style-captionM text-textColorText3">광고성 푸시 동의</p>
                <p className="text-style-title3 text-brown-80%-main mt-4px">{perm.ad_consent}명</p>
                <p className="text-style-captionM text-textColorText3 mt-4px">
                  그중 휴대폰 알림 거부 {perm.ad_consent_but_denied}명
                </p>
              </div>
            </div>

            <div className="mt-12px flex flex-col gap-4px text-style-body2 text-textColorText2">
              <div className="flex justify-between">
                <span>가입 직후 권한 화면</span>
                <span>
                  {perm.signup.answered}명 중 {perm.signup.granted}명 허용
                  {perm.signup.answered > 0 &&
                    ` (${Math.round((perm.signup.granted / perm.signup.answered) * 100)}%)`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>기존 회원 (앱에서 한 번 묻기)</span>
                <span>
                  {perm.home.answered}명 중 {perm.home.granted}명 허용
                  {perm.home.answered > 0 &&
                    ` (${Math.round((perm.home.granted / perm.home.answered) * 100)}%)`}
                </span>
              </div>
            </div>

            <p className="text-style-captionM text-textColorText3 mt-12px">
              ※ 휴대폰 알림 허용은 2026-11-01부터 쌓입니다
              {perm.since ? ` (첫 기록 ${perm.since})` : ''}. 그 전 회원과 아직 답하지 않은
              회원은 '모름'입니다. 광고 발송은 광고성 푸시 동의만 봅니다.
            </p>
          </div>
        )}

        <div className="max-w-560px bg-white rounded-12px border border-brandPrimary-10% p-24px flex flex-col gap-20px">
          <div className="flex items-center justify-between bg-backgroundColorBg2 rounded-8px px-16px py-12px">
            <span className="text-style-body2 text-textColorText2">
              현재 발송 대상
            </span>
            <span className="text-style-title3 text-brown-80%-main">
              {audience === null ? '…' : `${audience}명`}
            </span>
          </div>

          <AdminInput
            label="제목"
            required
            value={title}
            maxLength={100}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="예) 테이스티 소식 🍽"
          />

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              본문 *
            </label>
            <textarea
              value={body}
              maxLength={500}
              onChange={(e) => setBody(e.target.value)}
              placeholder="예) 새로운 맛집들이 기다리고 있어요! 지금 확인해보세요"
              rows={4}
              className="w-full py-10px px-12px border border-brandPrimary-20% rounded-8px text-style-body2 text-textColorText2 bg-transparent focus:outline-none focus:border-brandPrimary placeholder:text-textColorText2 resize-none"
            />
            <span className="text-style-captionM text-textColorText3 mt-4px block text-right">
              {body.length}/500
            </span>
          </div>

          <button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className="w-full py-12px rounded-8px bg-brandPrimary text-white text-style-body1 font-medium disabled:opacity-50 transition-opacity"
          >
            {isSending ? '발송 중…' : '발송하기'}
          </button>

          <p className="text-style-captionM text-textColorText3">
            ※ 설정에서 푸시 알림을 끈 유저에게는 발송되지 않습니다.
          </p>
        </div>
      </main>
    </div>
  )
}

export default AdminPush
